import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// useLanguage import removed - not currently used
import { useTheme } from '../../contexts/ThemeContext';
import { useNotificationsStore } from '../../stores/notificationsStore';
import { UserProfile } from './UserProfile';
// LanguageSelector import removed - not currently used
import TimeTrackingWidget from '../widgets/TimeTrackingWidget';
import { OctoAIChatbot } from '../octoai/OctoAIChatbot';
import { NotificationBell } from '../notifications/NotificationBell';
import api from '../../services/api.service';
import { stageInfo, stageLabel } from '../../constants/candidateStage';
import '../../styles/modern-design.css';

// Breadcrumb-Titel je Bereich (erstes Pfadsegment); vorher stand überall „Übersicht".
const BEREICHE: Record<string, string> = {
  dashboard: 'Übersicht',
  email: 'E-Mail',
  'calendar-tasks': 'Kalender',
  companies: 'Unternehmen',
  recruiting: 'Kandidaten',
  'job-orders': 'Stellenaufträge',
  employees: 'Mitarbeiter',
  documents: 'Dokumente',
  analytics: 'Analytics',
  settings: 'Einstellungen',
};

export const Header: React.FC = () => {
  const location = useLocation();
  const bereich = BEREICHE[location.pathname.split('/')[1]] || 'Übersicht';
  const [openDropdown, setOpenDropdown] = useState<'notifications' | 'profile' | 'timeTracking' | null>(null);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showOctoAI, setShowOctoAI] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bellShake, setBellShake] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    candidates: any[];
    contacts: any[];
    companies: any[];
    documents: any[];
  }>({
    candidates: [],
    contacts: [],
    companies: [],
    documents: []
  });
  const { user: _user } = useAuth();
  const { theme: _theme } = useTheme();
  const navigate = useNavigate();

  // Refs for click-outside detection
  const notificationsDropdownRef = useRef<HTMLDivElement>(null);
  const notificationsButtonRef = useRef<HTMLButtonElement>(null);

  // Function to toggle dropdowns
  const toggleDropdown = (dropdown: 'notifications' | 'profile' | 'timeTracking') => {
    setOpenDropdown(prev => prev === dropdown ? null : dropdown);
  };

  // Notifications from store
  const { getFilteredNotifications, getUnreadCount } = useNotificationsStore();
  const notifications = getFilteredNotifications();
  const unreadCount = getUnreadCount();
  const prevUnreadCount = useRef(unreadCount);

  // Debounced Search Function
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults({
        candidates: [],
        contacts: [],
        companies: [],
        documents: []
      });
      return;
    }

    const debounce = setTimeout(async () => {
      // Search in parallel
      const [candidatesRes, contactsRes, companiesRes, docsRes] = await Promise.all([
        api.getCandidates({ limit: 100, search: searchQuery } as any),
        api.getContacts({ limit: 100, search: searchQuery } as any),
        api.getCompanies({ limit: 100, search: searchQuery } as any),
        api.getDocuments({ limit: 100, search: searchQuery } as any)
      ]);

      setSearchResults({
        candidates: Array.isArray(candidatesRes.data) ? candidatesRes.data : ((candidatesRes.data as any)?.data || []),
        contacts: Array.isArray(contactsRes.data) ? contactsRes.data : ((contactsRes.data as any)?.data || []),
        companies: Array.isArray(companiesRes.data) ? companiesRes.data : ((companiesRes.data as any)?.data || []),
        documents: Array.isArray(docsRes.data) ? docsRes.data : ((docsRes.data as any)?.data || [])
      });
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
        setShowOctoAI(false);
        setOpenDropdown(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Bell shake animation when new notifications arrive
  useEffect(() => {
    // Trigger animation if unread count increased
    if (unreadCount > prevUnreadCount.current) {
      setBellShake(true);

      // Reset animation after 820ms (duration of shake)
      const timer = setTimeout(() => {
        setBellShake(false);
      }, 820);

      return () => clearTimeout(timer);
    }

    // Update previous count
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);


  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationsDropdownRef.current &&
        notificationsButtonRef.current &&
        !notificationsDropdownRef.current.contains(event.target as Node) &&
        !notificationsButtonRef.current.contains(event.target as Node)
      ) {
        if (openDropdown === 'notifications') {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  return (
    <>
      <header style={{
        height: '64px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        background: 'var(--glass)',
        backdropFilter: 'blur(16px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        {/* Left Section - Breadcrumbs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Home Icon */}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>
              <path d="M2 6L8 1.5L14 6V13.5C14 13.8978 13.842 14.2794 13.5607 14.5607C13.2794 14.842 12.8978 15 12.5 15H3.5C3.10218 15 2.72064 14.842 2.43934 14.5607C2.15804 14.2794 2 13.8978 2 13.5V6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 15V8H10V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem', fontWeight: 500 }}>
              Dashboard
            </span>
            <span style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>/</span>
            <span style={{ color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: 600 }}>
              {bereich}
            </span>
          </nav>
        </div>

        {/* Center Section - Modern Search Bar */}
        <div style={{ flex: 1, maxWidth: '500px', margin: '0 var(--spacing-xl)' }}>
          <button
            onClick={() => setShowCommandPalette(true)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-sm)',
              cursor: 'pointer',
              textAlign: 'left',
              background: 'var(--glass-subtle, rgba(255, 255, 255, 0.03))',
              border: '1px solid var(--glass-border, rgba(255, 255, 255, 0.08))',
              borderRadius: '12px',
              padding: '10px 14px',
              transition: 'all 0.2s ease',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--glass-hover, rgba(255, 255, 255, 0.08))';
              e.currentTarget.style.borderColor = 'var(--border-primary, rgba(255, 255, 255, 0.12))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--glass-subtle, rgba(255, 255, 255, 0.03))';
              e.currentTarget.style.borderColor = 'var(--glass-border, rgba(255, 255, 255, 0.08))';
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--accent-primary, #8b5cf6)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--glass-border, rgba(255, 255, 255, 0.08))';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--text-tertiary, rgba(255, 255, 255, 0.5))' }}>
              <path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 14L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{
              flex: 1,
              color: 'var(--text-tertiary, rgba(255, 255, 255, 0.5))',
              fontSize: '0.875rem',
              fontWeight: 400
            }}>
              Suchen...
            </span>
            <kbd style={{
              padding: '4px 8px',
              background: 'var(--glass-subtle, rgba(255, 255, 255, 0.05))',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-tertiary, rgba(255, 255, 255, 0.5))',
              border: '1px solid var(--glass-border, rgba(255, 255, 255, 0.1))',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Right Section - Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Time Tracking Widget */}
          <TimeTrackingWidget
            isOpen={openDropdown === 'timeTracking'}
            onToggle={() => toggleDropdown('timeTracking')}
          />

          {/* New Notification System */}
          <NotificationBell />

          {/* Notifications */}
          <div style={{ position: 'relative' }}>
            <button
              ref={notificationsButtonRef}
              className="btn btn-ghost"
              onClick={() => toggleDropdown('notifications')}
              style={{ padding: 'var(--spacing-sm)', position: 'relative' }}
            >
              <motion.div
                animate={bellShake ? {
                  rotate: [0, -15, 15, -15, 15, -10, 10, -5, 5, 0],
                } : {}}
                transition={{
                  duration: 0.82,
                  ease: "easeInOut"
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <path
                    d="M10 2.5C8.5 2.5 7.25 3.75 7.25 5.25V5.875C5.375 6.75 4 8.625 4 10.875V14L2.5 15.5V16.25H17.5V15.5L16 14V10.875C16 8.625 14.625 6.75 12.75 5.875V5.25C12.75 3.75 11.5 2.5 10 2.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11.5 16.25C11.5 17.0784 10.8284 17.75 10 17.75C9.17157 17.75 8.5 17.0784 8.5 16.25"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 8,
                  height: 8,
                  background: 'var(--accent-danger)',
                  borderRadius: '50%'
                }} />
              )}
            </button>

            <AnimatePresence>
              {openDropdown === 'notifications' && (
                <motion.div
                  ref={notificationsDropdownRef}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + var(--spacing-sm))',
                    right: 0,
                    width: '320px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-lg)',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    padding: 'var(--spacing-md)',
                    borderBottom: '1px solid var(--glass-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <h3 style={{ fontSize: '0.925rem', fontWeight: 600 }}>Benachrichtigungen</h3>
                    {unreadCount > 0 && (
                      <span style={{
                        background: 'var(--accent-danger)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 600
                      }}>
                        {unreadCount}
                      </span>
                    )}
                  </div>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{
                        padding: 'var(--spacing-xl)',
                        textAlign: 'center',
                        color: 'var(--text-tertiary)'
                      }}>
                        <p style={{ fontSize: '0.875rem' }}>Keine Benachrichtigungen</p>
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notif) => (
                        <div
                          key={notif.id}
                          style={{
                            padding: 'var(--spacing-md)',
                            borderBottom: '1px solid var(--glass-border)',
                            cursor: 'pointer',
                            transition: 'background var(--transition-fast)',
                            background: notif.unread ? 'rgba(139, 92, 246, 0.05)' : 'transparent'
                          }}
                          className="hover-bg"
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <div style={{
                              fontSize: '1.2rem',
                              flexShrink: 0,
                              marginTop: '2px'
                            }}>
                              {notif.type === 'email' ? '📧' :
                               notif.type === 'chat' ? '💬' :
                               notif.type === 'meeting' ? '📅' :
                               notif.type === 'task' ? '✅' :
                               notif.type === 'candidate' ? '👤' :
                               notif.type === 'document' ? '📄' :
                               notif.type === 'alert' ? '⚠️' : '🔔'}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                <p style={{
                                  fontSize: '0.875rem',
                                  fontWeight: notif.unread ? 600 : 500,
                                  flex: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {notif.title}
                                </p>
                                {notif.unread && (
                                  <span style={{
                                    width: '6px',
                                    height: '6px',
                                    background: 'var(--accent-primary)',
                                    borderRadius: '50%',
                                    flexShrink: 0
                                  }} />
                                )}
                              </div>
                              <p style={{
                                fontSize: '0.75rem',
                                color: 'var(--text-tertiary)',
                                marginBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {notif.description}
                              </p>
                              <p style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                                {notif.time}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Language Selector - Hidden for now */}
          {/* <LanguageSelector /> */}

          {/* User Profile */}
          <UserProfile
            isOpen={openDropdown === 'profile'}
            onToggle={() => toggleDropdown('profile')}
          />
        </div>
      </header>

      {/* Command Palette */}
      <AnimatePresence>
        {showCommandPalette && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCommandPalette(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                zIndex: 999
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              style={{
                position: 'fixed',
                top: '15%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '90%',
                maxWidth: '640px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--glass-border)',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                zIndex: 1000,
                overflow: 'hidden'
              }}
            >
              <div style={{
                padding: 'var(--spacing-lg)',
                borderBottom: '1px solid var(--glass-border)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}>
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--accent-primary, #8b5cf6)' }}>
                  <path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 14L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  placeholder="Suche nach Kandidaten, Kontakten, Unternehmen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--text-primary)',
                    fontSize: '1rem',
                    fontWeight: 400
                  }}
                />
                <kbd style={{
                  padding: '4px 8px',
                  background: 'var(--glass-subtle)',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--text-tertiary)',
                  border: '1px solid var(--glass-border)'
                }}>
                  ESC
                </kbd>
              </div>
              <div style={{ padding: 'var(--spacing-md)' }}>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {/* Quick Actions */}
                  {searchQuery.length < 2 && (
                    <>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', padding: '0 var(--spacing-md)', marginBottom: 'var(--spacing-sm)' }}>
                        Schnellaktionen
                      </div>
                      {[
                        { id: 'new-candidate', label: 'Kandidat anlegen', icon: '👤', action: () => { navigate('/recruiting?action=new-candidate'); setShowCommandPalette(false); } },
                        { id: 'new-contact', label: 'Kontakt anlegen', icon: '📇', action: () => { navigate('/contacts?action=new'); setShowCommandPalette(false); } },
                        { id: 'new-appointment', label: 'Kalendereintrag', icon: '📅', action: () => { navigate('/calendar-tasks?action=new-appointment'); setShowCommandPalette(false); } },
                        { id: 'upload-document', label: 'Dokument hochladen', icon: '📄', action: () => { navigate('/documents?action=upload'); setShowCommandPalette(false); } }
                      ].map((action) => (
                        <div
                          key={action.id}
                          onClick={action.action}
                          style={{
                            padding: 'var(--spacing-sm) var(--spacing-md)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-sm)',
                            transition: 'background var(--transition-fast)'
                          }}
                          className="hover-bg"
                        >
                          <span style={{ fontSize: '1rem' }}>{action.icon}</span>
                          <span style={{ flex: 1, fontSize: '0.875rem' }}>{action.label}</span>
                      <kbd style={{
                        padding: '2px 6px',
                        background: 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.75rem',
                        color: 'var(--text-tertiary)'
                      }}>
                        Enter
                      </kbd>
                    </div>
                      ))}
                    </>
                  )}

                  {/* Search Results */}
                  {searchQuery.length >= 2 && (
                    <>
                      {/* No Results */}
                      {searchResults.candidates.length === 0 &&
                       searchResults.contacts.length === 0 &&
                       searchResults.companies.length === 0 &&
                       searchResults.documents.length === 0 && (
                        <div style={{
                          padding: 'var(--spacing-xl)',
                          textAlign: 'center',
                          color: 'var(--text-tertiary)'
                        }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ margin: '0 auto var(--spacing-md)', opacity: 0.3 }}>
                            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <p style={{ fontSize: '0.925rem', fontWeight: 500, marginBottom: 'var(--spacing-xs)' }}>
                            Keine Ergebnisse gefunden
                          </p>
                          <p style={{ fontSize: '0.825rem', color: 'var(--text-tertiary)' }}>
                            Versuchen Sie es mit anderen Suchbegriffen
                          </p>
                        </div>
                      )}

                      {/* Candidates */}
                      {searchResults.candidates.length > 0 && (
                        <>
                          <div style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 var(--spacing-md)',
                            marginTop: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-xs)'
                          }}>
                            Kandidaten ({searchResults.candidates.length})
                          </div>
                          {searchResults.candidates.map(candidate => {
                            // Führend ist workflow_stage; `stage` ist abgelöst (zeigte z. B. „NEW" statt „Gesprochen").
                            const wfStage = (candidate as any).workflow_stage as string | undefined;
                            const talentStatus = wfStage ? stageLabel(wfStage, true) : null;

                            return (
                              <div
                                key={candidate.id}
                                onClick={() => {
                                  navigate('/recruiting', { state: { openCandidateId: candidate.id, activeTab: 'candidates' } });
                                  setShowCommandPalette(false);
                                }}
                                style={{
                                  padding: 'var(--spacing-sm) var(--spacing-md)',
                                  borderRadius: 'var(--radius-md)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 'var(--spacing-md)',
                                  transition: 'all 0.15s ease',
                                  border: '1px solid transparent'
                                }}
                                className="hover-bg"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--glass-border)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = 'transparent';
                                }}
                              >
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  background: 'rgba(139, 92, 246, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1rem'
                                }}>
                                  👤
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block' }}>
                                    {candidate.first_name} {candidate.last_name}
                                  </span>
                                  {candidate.city && (
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                      {candidate.city}{candidate.state ? `, ${candidate.state}` : ''}
                                    </span>
                                  )}
                                </div>
                                {talentStatus && (
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    background: `${stageInfo(wfStage).color}20`,
                                    color: stageInfo(wfStage).color,
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {talentStatus}
                                  </span>
                                )}
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.3, flexShrink: 0 }}>
                                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Contacts */}
                      {searchResults.contacts.length > 0 && (
                        <>
                          <div style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 var(--spacing-md)',
                            marginTop: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-xs)'
                          }}>
                            Kontakte ({searchResults.contacts.length})
                          </div>
                          {searchResults.contacts.map(contact => (
                            <div
                              key={contact.id}
                              onClick={() => {
                                navigate('/contacts', { state: { openContactId: contact.id, activeTab: 'people' } });
                                setShowCommandPalette(false);
                              }}
                              style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-md)',
                                transition: 'all 0.15s ease',
                                border: '1px solid transparent'
                              }}
                              className="hover-bg"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--glass-border)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'transparent';
                              }}
                            >
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: 'rgba(16, 185, 129, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1rem'
                              }}>
                                📇
                              </div>
                              <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500 }}>{contact.first_name} {contact.last_name}</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.3 }}>
                                <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Companies */}
                      {searchResults.companies.length > 0 && (
                        <>
                          <div style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 var(--spacing-md)',
                            marginTop: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-xs)'
                          }}>
                            Unternehmen ({searchResults.companies.length})
                          </div>
                          {searchResults.companies.map(company => {
                            // Classification Badge color
                            const getClassificationColor = (classification: string) => {
                              const colors: Record<string, string> = {
                                'A': '#10b981', // Emerald - Premium
                                'B': '#3b82f6', // Blue - Good
                                'C': '#f59e0b', // Amber - Standard
                                'D': '#6b7280', // Gray - Low priority
                                'Blacklist': '#ef4444' // Red - Blacklisted
                              };
                              return colors[classification] || '#6b7280';
                            };
                            const classification = company.classification || company.rating;

                            return (
                              <div
                                key={company.id}
                                onClick={() => {
                                  navigate('/contacts', { state: { openCompanyId: company.id, activeTab: 'companies' } });
                                  setShowCommandPalette(false);
                                }}
                                style={{
                                  padding: 'var(--spacing-sm) var(--spacing-md)',
                                  borderRadius: 'var(--radius-md)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 'var(--spacing-md)',
                                  transition: 'all 0.15s ease',
                                  border: '1px solid transparent'
                                }}
                                className="hover-bg"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--glass-border)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = 'transparent';
                                }}
                              >
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  background: 'rgba(59, 130, 246, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1rem'
                                }}>
                                  🏢
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block' }}>
                                    {company.name}
                                  </span>
                                  {(company.billing_city || company.city) && (
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                      {company.billing_city || company.city}{(company.billing_state || company.state) ? `, ${company.billing_state || company.state}` : ''}
                                    </span>
                                  )}
                                </div>
                                {classification && (
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    background: `${getClassificationColor(classification)}20`,
                                    color: getClassificationColor(classification),
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {classification}
                                  </span>
                                )}
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.3, flexShrink: 0 }}>
                                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Documents */}
                      {searchResults.documents.length > 0 && (
                        <>
                          <div style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'var(--text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '0 var(--spacing-md)',
                            marginTop: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-xs)'
                          }}>
                            Dokumente ({searchResults.documents.length})
                          </div>
                          {searchResults.documents.map(document => (
                            <div
                              key={document.id}
                              onClick={() => { navigate(`/documents/${document.id}`); setShowCommandPalette(false); }}
                              style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-md)',
                                transition: 'all 0.15s ease',
                                border: '1px solid transparent'
                              }}
                              className="hover-bg"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = 'var(--glass-border)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'transparent';
                              }}
                            >
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: 'rgba(245, 158, 11, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1rem'
                              }}>
                                📄
                              </div>
                              <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 500 }}>{document.name}</span>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.3 }}>
                                <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* OctoAI Chatbot */}
      <OctoAIChatbot
        isOpen={showOctoAI}
        onClose={() => setShowOctoAI(false)}
      />

      <style>{`
        .hover-bg:hover {
          background: var(--glass-hover);
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
};